/**
 * The offline operation queue — BUILD_PLAN 1.11, docs/sync-state-machine.md §3.
 *
 * Every mutation a member makes at the court becomes a `SyncOp` written to
 * IndexedDB **before** the UI confirms it (Rule L-1). A process kill between
 * the tap and the write must never produce a phantom confirmation, and the only
 * way to guarantee that is for the durable write to come first.
 *
 * This file owns the queue. Sending it is `sender.ts`; the two are separate so
 * the storage rules can be tested without a network and the retry rules without
 * a database.
 *
 * Domain B — sign-up, commit, allocation — never comes near this queue
 * (Rule A/B-1). Those touch scarce shared resources, and telling a member they
 * hold a slot and retracting it later is worse than telling them to find signal.
 */

import Dexie, { type Table } from "dexie";

export type OpType =
  | "create_game"
  | "edit_game"
  | "delete_game"
  | "set_attendance"
  | "set_rotation";

export type OpState =
  | "pending"
  | "inflight"
  | "synced"
  | "conflict"
  | "failed"
  | "superseded";

export interface SyncOp {
  clientUuid: string;
  deviceId: string;
  opSeq: number;
  opType: OpType;
  entityId: string | null;
  baseVersion: number | null;
  payload: Record<string, unknown>;
  /** Device clock at composition. Untrusted; recorded for skew detection (O-1). */
  clientTime: string;
  state: OpState;
  attempts: number;
  nextAttemptAt: number;
  error: string | null;
  /** Set when this op was collapsed into another before transmission (C-2). */
  supersededBy?: string;
}

class QueueDatabase extends Dexie {
  ops!: Table<SyncOp, string>;

  constructor(name = "core-baddy") {
    super(name);
    this.version(1).stores({
      // client_uuid is the primary key; the rest are the indexes the sender and
      // the indicator actually query on.
      ops: "clientUuid, opSeq, state, [state+nextAttemptAt], entityId",
    });
  }
}

/** Stable per install. The op_seq counter is only monotonic within a device. */
const DEVICE_ID_KEY = "core-baddy.device-id";

export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export interface QueueCounts {
  pending: number;
  inflight: number;
  conflict: number;
  failed: number;
  /** Age of the oldest unsent op, in ms. ST-3 puts this in the indicator. */
  oldestPendingAgeMs: number | null;
}

export class SyncQueue {
  private readonly db: QueueDatabase;
  private readonly device: string;

  constructor(options: { databaseName?: string; deviceId?: string } = {}) {
    this.db = new QueueDatabase(options.databaseName);
    this.device = options.deviceId ?? deviceId();
  }

  close(): Promise<void> {
    this.db.close();
    return Promise.resolve();
  }

  /**
   * Rule L-1 — persist before confirm. The caller awaits this before showing
   * anything, so a confirmation is only ever shown for an op that survived.
   */
  async enqueue(input: {
    opType: OpType;
    payload: Record<string, unknown>;
    entityId?: string | null;
    baseVersion?: number | null;
    clientUuid?: string;
  }): Promise<SyncOp> {
    const op: SyncOp = {
      clientUuid: input.clientUuid ?? crypto.randomUUID(),
      deviceId: this.device,
      opSeq: await this.nextSeq(),
      opType: input.opType,
      entityId: input.entityId ?? null,
      baseVersion: input.baseVersion ?? null,
      payload: input.payload,
      clientTime: new Date().toISOString(),
      state: "pending",
      attempts: 0,
      nextAttemptAt: 0,
      error: null,
    };
    await this.db.ops.add(op);
    return op;
  }

  /**
   * Monotonic per device. Derived from the stored maximum rather than a
   * counter in memory, so it survives a reload mid-session.
   */
  private async nextSeq(): Promise<number> {
    const last = await this.db.ops.orderBy("opSeq").last();
    return (last?.opSeq ?? 0) + 1;
  }

  /**
   * §4 — local collapsing, before transmission only.
   *
   * Rule C-1 is the important half: once an op is inflight or synced, later
   * changes are new ops. Collapsing a chain the server has already seen would
   * erase an audit trail it holds, which is the opposite of the point.
   *
   * Returns the ops that were superseded, so a caller can report what happened.
   */
  async collapse(): Promise<SyncOp[]> {
    const superseded: SyncOp[] = [];

    await this.db.transaction("rw", this.db.ops, async () => {
      const pending = await this.db.ops.where("state").equals("pending").sortBy("opSeq");

      // Group the pending chain by the entity it concerns. A create is keyed by
      // its own client_uuid, since the entity has no server id yet.
      const chains = new Map<string, SyncOp[]>();
      for (const op of pending) {
        const key = op.opType === "create_game" ? op.clientUuid : (op.entityId ?? op.clientUuid);
        chains.set(key, [...(chains.get(key) ?? []), op]);
      }

      for (const [, chain] of chains) {
        if (chain.length < 2) continue;

        const create = chain.find((o) => o.opType === "create_game");
        const lastDelete = [...chain].reverse().find((o) => o.opType === "delete_game");
        const edits = chain.filter((o) => o.opType === "edit_game");

        // create → delete: nothing is transmitted at all.
        if (create && lastDelete) {
          for (const op of chain) {
            await this.db.ops.update(op.clientUuid, {
              state: "superseded",
              supersededBy: lastDelete.clientUuid,
            });
            superseded.push(op);
          }
          continue;
        }

        // create → edit [→ edit]: one create carrying the final values.
        if (create && edits.length > 0) {
          const merged = edits.reduce<Record<string, unknown>>(
            (payload, edit) => ({ ...payload, ...edit.payload }),
            { ...create.payload },
          );
          await this.db.ops.update(create.clientUuid, { payload: merged });
          for (const edit of edits) {
            await this.db.ops.update(edit.clientUuid, {
              state: "superseded",
              supersededBy: create.clientUuid,
            });
            superseded.push(edit);
          }
          continue;
        }

        // edit → edit on a synced entity: one edit, keeping the earliest
        // base_version, because that is the version the chain was composed
        // against.
        if (!create && edits.length > 1) {
          const [first, ...rest] = edits;
          const merged = rest.reduce<Record<string, unknown>>(
            (payload, edit) => ({ ...payload, ...edit.payload }),
            { ...first.payload },
          );
          await this.db.ops.update(first.clientUuid, { payload: merged });
          for (const edit of rest) {
            await this.db.ops.update(edit.clientUuid, {
              state: "superseded",
              supersededBy: first.clientUuid,
            });
            superseded.push(edit);
          }
          continue;
        }

        // edit → delete on a synced entity: the delete subsumes the edits.
        if (!create && lastDelete && edits.length > 0) {
          for (const edit of edits) {
            await this.db.ops.update(edit.clientUuid, {
              state: "superseded",
              supersededBy: lastDelete.clientUuid,
            });
            superseded.push(edit);
          }
        }
      }
    });

    return superseded;
  }

  /** The next batch to send: pending, due, in op_seq order. */
  async due(now = Date.now(), limit = 50): Promise<SyncOp[]> {
    const pending = await this.db.ops.where("state").equals("pending").sortBy("opSeq");
    return pending.filter((op) => op.nextAttemptAt <= now).slice(0, limit);
  }

  async markInflight(clientUuids: readonly string[]): Promise<void> {
    await this.db.transaction("rw", this.db.ops, async () => {
      for (const id of clientUuids) {
        await this.db.ops.update(id, { state: "inflight" });
      }
    });
  }

  async update(clientUuid: string, changes: Partial<SyncOp>): Promise<void> {
    await this.db.ops.update(clientUuid, changes);
  }

  async get(clientUuid: string): Promise<SyncOp | undefined> {
    return this.db.ops.get(clientUuid);
  }

  async all(): Promise<SyncOp[]> {
    return this.db.ops.orderBy("opSeq").toArray();
  }

  async byState(state: OpState): Promise<SyncOp[]> {
    return this.db.ops.where("state").equals(state).sortBy("opSeq");
  }

  /**
   * Rule I-3 — no op is ever discarded by the system. Only terminal states are
   * removed, and only on the user's instruction; `pending`, `inflight`,
   * `conflict` and `failed` all stay until a human deals with them.
   *
   * ST-1 — unsynced ops are never evicted for quota either. If storage pressure
   * arises, cached read-only data goes first.
   */
  async forgetSettled(): Promise<number> {
    return this.db.ops.where("state").anyOf("synced", "superseded").delete();
  }

  async counts(now = Date.now()): Promise<QueueCounts> {
    const all = await this.all();
    const unsent = all.filter((op) => op.state === "pending" || op.state === "inflight");
    const oldest = unsent.reduce<number | null>((acc, op) => {
      const age = now - Date.parse(op.clientTime);
      return acc === null || age > acc ? age : acc;
    }, null);

    return {
      pending: all.filter((op) => op.state === "pending").length,
      inflight: all.filter((op) => op.state === "inflight").length,
      conflict: all.filter((op) => op.state === "conflict").length,
      failed: all.filter((op) => op.state === "failed").length,
      oldestPendingAgeMs: oldest,
    };
  }

  /**
   * The escape hatch from §10 and ST-2: after repeated failure, the member can
   * take their results somewhere else rather than watch a spinner. Plain JSON,
   * everything unsent, no server involved.
   */
  async exportUnsent(): Promise<string> {
    const ops = (await this.all()).filter(
      (op) => op.state !== "synced" && op.state !== "superseded",
    );
    return JSON.stringify({ deviceId: this.device, exportedAt: new Date().toISOString(), ops }, null, 2);
  }
}
